/**
 * KeyboardShortcutsController.js
 * Handles all global keyboard shortcuts for the application.
 * 
 * Note: KeyboardController.js is separate - it handles WASD tracking for nudging.
 */

/**
 * Initialize keyboard shortcuts with action callbacks.
 * @param {Object} callbacks - Object containing all action callbacks
 * @param {Function} callbacks.undo - Undo last action
 * @param {Function} callbacks.redo - Redo last undone action
 * @param {Function} callbacks.selectAll - Select all items (context-dependent)
 * @param {Function} callbacks.copy - Copy selected items
 * @param {Function} callbacks.cut - Cut selected items
 * @param {Function} callbacks.paste - Paste from clipboard
 * @param {Function} callbacks.duplicate - Duplicate selected items
 * @param {Function} callbacks.delete - Delete selected items (normal)
 * @param {Function} callbacks.deleteWithShift - Delete with Shift held (clear routing)
 * @param {Function} callbacks.escape - Handle escape key (close modals, deselect)
 * @param {Function} callbacks.zoomIn - Zoom in at center
 * @param {Function} callbacks.zoomOut - Zoom out at center
 * @param {Function} callbacks.resetZoom - Reset zoom to default
 * @param {Function} callbacks.toggleLibrary - Toggle library panel
 * @param {Function} callbacks.editDefinition - Edit device definition
 * @param {Function} callbacks.editInstanceLayout - Edit instance layout
 * @param {Function} callbacks.save - Save patch
 * @param {Function} callbacks.open - Open/load patch
 * @param {Function} callbacks.newPatch - Create new patch
 * @param {Function} callbacks.openSettings - Open patch settings
 * @param {Function} callbacks.toggleHelp - Toggle help overlay
 * @returns {Function} Cleanup function to remove the event listener
 */
export function initKeyboardShortcuts(callbacks) {
    const {
        undo,
        redo,
        selectAll,
        copy,
        cut,
        paste,
        duplicate,
        delete: deleteAction,
        deleteWithShift,
        escape,
        zoomIn,
        zoomOut,
        resetZoom,
        toggleLibrary,
        editDefinition,
        editInstanceLayout,
        save,
        open,
        newPatch,
        openSettings,
        toggleHelp
    } = callbacks;

    function handleKeyDown(e) {
        // Skip if user is typing in an input or select
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

        // Ctrl+Z: Undo
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            undo?.();
            return;
        }
        
        // Ctrl+Y or Ctrl+Shift+Z: Redo
        if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey) || (e.key === 'Z' && e.shiftKey))) {
            e.preventDefault();
            redo?.();
            return;
        }

        // Ctrl+A: Select all (context-dependent)
        if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
            e.preventDefault();
            selectAll?.();
            return;
        }
        
        // Ctrl+C: Copy selected item(s)
        if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
            e.preventDefault();
            copy?.();
            return;
        }
        
        // Ctrl+X: Cut selected item(s)
        if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
            e.preventDefault();
            cut?.();
            return;
        }
        
        // Ctrl+V: Paste item(s)
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            e.preventDefault();
            paste?.();
            return;
        }
        
        // Ctrl+D: Duplicate selected item(s)
        if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
            e.preventDefault();
            duplicate?.();
            return;
        }
        
        // Ctrl+,: Patch Settings
        if ((e.ctrlKey || e.metaKey) && e.key === ',') {
            e.preventDefault();
            openSettings?.();
            return;
        }

        // Delete or Backspace: Delete selected items
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            if (e.shiftKey) {
                // Shift+Delete: Clear routing
                deleteWithShift?.();
            } else {
                deleteAction?.();
            }
            return;
        }

        // Escape: Close modals, deselect
        if (e.key === 'Escape') {
            escape?.();
            return;
        }

        // +/=: Zoom in
        if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            zoomIn?.();
            return;
        }

        // -/_: Zoom out
        if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            zoomOut?.();
            return;
        }

        // 0: Reset zoom
        if (e.key === '0') {
            e.preventDefault();
            resetZoom?.();
            return;
        }
        
        // L: Toggle library panel
        if (e.key === 'l' || e.key === 'L') {
            e.preventDefault();
            toggleLibrary?.();
            return;
        }
        
        // E: Edit definition (template) of selected item
        if (e.key === 'e' || e.key === 'E') {
            e.preventDefault();
            editDefinition?.();
            return;
        }
        
        // I: Edit instance layout (only for canvas devices)
        if (e.key === 'i' || e.key === 'I') {
            e.preventDefault();
            editInstanceLayout?.();
            return;
        }
        
        // Ctrl+S: Save patch
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            save?.();
            return;
        }
        
        // Ctrl+O: Open/Load patch
        if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
            e.preventDefault();
            open?.();
            return;
        }
        
        // Ctrl+N: New patch
        if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
            e.preventDefault();
            newPatch?.();
            return;
        }
        
        // ?: Toggle help overlay
        if (e.key === '?') {
            e.preventDefault();
            toggleHelp?.();
            return;
        }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Return cleanup function
    return () => {
        document.removeEventListener('keydown', handleKeyDown);
    };
}
